import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import type { ConsoleSection, TeamMember } from '../../lib/api/operatorConsole';
import { PermissionPicker } from './PermissionPicker';
import { sectionLabel } from './sectionLabels';

interface Props {
  member: TeamMember;
  isSelf: boolean;
  sections: ConsoleSection[];
  presets: Record<string, ConsoleSection[]>;
  busy: boolean;
  onSave: (member: TeamMember, permissions: ConsoleSection[]) => Promise<boolean>;
  onResetPassword: (member: TeamMember) => void;
  onRevoke: (member: TeamMember) => void;
}

function lastSeen(member: TeamMember): string {
  if (!member.last_seen_at) return 'never signed in';
  return new Date(member.last_seen_at).toLocaleString();
}

export function TeamMemberRow({
  member,
  isSelf,
  sections,
  presets,
  busy,
  onSave,
  onResetPassword,
  onRevoke,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ConsoleSection[]>(member.permissions);
  const isOwner = member.role === 'owner';

  async function save() {
    const ok = await onSave(member, draft);
    if (ok) setEditing(false);
  }

  return (
    <li className="border-b border-[var(--co-border)] px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-[13px] font-medium text-[var(--co-text)]">
              {member.email}
            </span>
            {isOwner && (
              <span className="rounded-full bg-[var(--co-bg)] px-2 py-[2px] text-[11px] text-[var(--co-muted)]">
                owner
              </span>
            )}
            {isSelf && <span className="text-[11px] text-[var(--co-muted)]">you</span>}
          </div>

          <div className="mt-1 text-[12px] text-[var(--co-muted)]">
            {isOwner ? (
              'Full access to every section'
            ) : member.permissions.length === 0 ? (
              'No sections yet — they can sign in and see the dashboard only'
            ) : (
              member.permissions.map(sectionLabel).join(', ')
            )}
          </div>

          <div className="mt-1 text-[11px] text-[var(--co-muted)]">
            {lastSeen(member)}
            {member.two_factor_enrolled && ' · 2FA on'}
            {member.must_change_password && ' · must set a new password'}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          {!isOwner && (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setDraft(member.permissions);
                setEditing((v) => !v);
              }}
            >
              {editing ? 'Cancel' : 'Edit access'}
            </Button>
          )}
          {/* Not for yourself: a reset wipes every console token of that
              account, so an owner doing it to their own row is signed out on
              the spot — with the new password on a screen they just lost. */}
          {!isSelf && (
            <Button variant="ghost" disabled={busy} onClick={() => onResetPassword(member)}>
              Reset password
            </Button>
          )}
          {!isSelf && (
            <Button variant="ghost" disabled={busy} onClick={() => onRevoke(member)}>
              Remove
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-3 rounded-lg bg-[var(--co-bg)] p-3">
          <PermissionPicker
            sections={sections}
            presets={presets}
            value={draft}
            onChange={setDraft}
            disabled={busy}
          />
          <div className="mt-3 flex gap-2">
            <Button disabled={busy} onClick={() => void save()}>
              Save access
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
